'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface NewsArticle {
  id: string;
  title: string;
  source: string;
  time: string;
  category: string;
  image: string;
  alt: string;
  impact: 'high' | 'medium' | 'low';
  excerpt: string;
  url?: string;
}

interface NewsGridProps {
  articles: NewsArticle[];
}

export default function NewsGrid({ articles }: NewsGridProps) {
  const impactColors = {
    high: 'bg-secondary/20 text-secondary border-secondary',
    medium: 'bg-accent/20 text-accent border-accent',
    low: 'bg-primary/20 text-primary border-primary',
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {articles.map((article) => (
        <article
          key={article.id}
          className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary transition-all group"
        >
          <Link 
            href={article.url && article.url.startsWith('http') ? article.url : `https://${article.url || 'www.moneymine.in/news'}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <div className="relative h-48 overflow-hidden">
              <AppImage
                src={article.image}
                alt={article.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute top-4 left-4 flex gap-2">
                <span className="px-3 py-1 bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs font-semibold rounded-full">
                  {article.category}
                </span>
                <span
                  className={`px-3 py-1 backdrop-blur-sm text-xs font-semibold rounded-full border ${
                    impactColors[article.impact]
                  }`}
                >
                  {article.impact.toUpperCase()}
                </span>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-lg font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                {article.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="NewspaperIcon" size={14} />
                  {article.source}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Icon name="ClockIcon" size={14} />
                  {article.time}
                </span>
              </div>
            </div>
          </Link>
        </article>
      ))}
    </div>
  );
}